import {
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  MessageBar,
  MessageBarBody,
  makeStyles,
  OverlayDrawer,
  Spinner,
  tokens,
  useId,
} from "@fluentui/react-components"
import { Dismiss24Regular } from "@fluentui/react-icons"
import type { FormEventHandler, ReactNode } from "react"
import { es } from "../i18n/es"

const useStyles = makeStyles({
  form: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
})

export interface AppDrawerFormProps {
  open: boolean
  title: string
  // Pasar el handleSubmit(...) de react-hook-form
  onSubmit: FormEventHandler<HTMLFormElement>
  onClose: () => void
  submitLabel?: string
  cancelLabel?: string
  submitting?: boolean
  // Error de servidor NO mapeable a campos → MessageBar dentro del formulario
  error?: string | null
  size?: "small" | "medium" | "large"
  children: ReactNode
}

// Drawer estándar para crear/editar — docs/05 §3: panel lateral overlay con
// formulario, sin navegar a otra página.
export const AppDrawerForm = ({
  open,
  title,
  onSubmit,
  onClose,
  submitLabel = es.common.save,
  cancelLabel = es.common.cancel,
  submitting = false,
  error = null,
  size = "medium",
  children,
}: AppDrawerFormProps) => {
  const styles = useStyles()
  const formId = useId("drawer-form")
  return (
    <OverlayDrawer
      position="end"
      size={size}
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) onClose()
      }}
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label={es.common.close}
              icon={<Dismiss24Regular />}
              onClick={onClose}
            />
          }
        >
          {title}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <form id={formId} className={styles.form} onSubmit={onSubmit} noValidate>
          {error ? (
            <MessageBar intent="error">
              <MessageBarBody>{error}</MessageBarBody>
            </MessageBar>
          ) : null}
          {children}
        </form>
      </DrawerBody>
      <DrawerFooter>
        <Button
          appearance="primary"
          type="submit"
          form={formId}
          disabled={submitting}
          icon={submitting ? <Spinner size="tiny" /> : undefined}
        >
          {submitLabel}
        </Button>
        <Button appearance="secondary" disabled={submitting} onClick={onClose}>
          {cancelLabel}
        </Button>
      </DrawerFooter>
    </OverlayDrawer>
  )
}
